/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isRunningStatus } from '../components/inactive/subscription-lock';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import type { AgencyModelReturnFindById } from '@services/requests/housien-models/AgencyModelReturnFindById';

export type ActivationStatus = 'LOADING' | 'SUCCESS' | 'TIMEOUT';

export interface UseSubscriptionActivationParams {
  agencyId: string;
  enabled: boolean;
  intervalMs?: number;
  maxAttempts?: number;
}

export interface UseSubscriptionActivationResult {
  status: ActivationStatus;
  agency: AgencyModelReturnFindById | null;
  retry: () => void;
}

const DEFAULT_INTERVAL_MS = 2000;
const DEFAULT_MAX_ATTEMPTS = 10;

/**
 * Applies a single reconciliation poll result to the activation state machine.
 *
 * @param result - The reconciled agency, or null when the call failed.
 * @param attempts - The number of poll attempts made so far (1-based).
 * @param maxAttempts - The maximum attempts before declaring a timeout.
 * @param setAgency - Setter invoked with the agency once it is running.
 * @param setStatus - Setter for the SUCCESS/TIMEOUT terminal status.
 * @param scheduleNext - Callback that schedules the next poll attempt.
 */
function applyPollResult(
  result: AgencyModelReturnFindById | null,
  attempts: number,
  maxAttempts: number,
  setAgency: (a: AgencyModelReturnFindById) => void,
  setStatus: (s: ActivationStatus) => void,
  scheduleNext: () => void,
) {
  if (result && isRunningStatus(result.effective_status)) { setAgency(result); setStatus('SUCCESS'); return; }
  if (attempts >= maxAttempts) { setStatus('TIMEOUT'); return; }
  scheduleNext();
}

/**
 * Bounded polling of the subscription reconciliation endpoint.
 *
 * Keeps the activation screen in LOADING until the subscription is running (active or trialing)
 * (SUCCESS) or the attempt budget is exhausted (TIMEOUT). Rejected calls do
 * not abort the loop; they simply consume one attempt.
 */
export function useSubscriptionActivation({
  agencyId,
  enabled,
  intervalMs = DEFAULT_INTERVAL_MS,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
}: UseSubscriptionActivationParams): UseSubscriptionActivationResult {
  const [status, setStatus] = useState<ActivationStatus>('LOADING');
  const [agency, setAgency] = useState<AgencyModelReturnFindById | null>(null);
  const [cycle, setCycle] = useState(0);
  const attemptsRef = useRef(0);

  const retry = useCallback(() => {
    attemptsRef.current = 0;
    setStatus('LOADING');
    setCycle((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!enabled || status !== 'LOADING') return;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      attemptsRef.current += 1;
      const result = await AgenciesService.reconcileSubscription(agencyId).catch(() => null);
      if (!active) return;
      applyPollResult(result, attemptsRef.current, maxAttempts, setAgency, setStatus, () => {
        timer = setTimeout(tick, intervalMs);
      });
    };

    void tick();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [agencyId, enabled, status, intervalMs, maxAttempts, cycle]);

  return { status, agency, retry };
}
